/**
 * MSW handlers for the review-and-run payroll demo.
 *
 * These intercept the real `api/payroll/v1/payrolls/{uuid}/` and `api/payroll/v1/team/` requests
 * and answer them with the wire-shaped fixtures, so the demo drives the actual generated hooks, the
 * adapter and the URLs — not a second code path that can agree with the design while the wiring is
 * wrong. The run endpoint answers 501 on the backend today; going live is deleting that handler,
 * not rewriting the hook.
 *
 * Mirrors `modules/Payroll/Overview/demo/demoHandlers.ts`.
 */

// msw is a devDependency but this file is only used by Storybook, which includes devDependencies in its build
// eslint-disable-next-line import/no-extraneous-dependencies
import { http, HttpResponse, delay } from 'msw';

import providerUnavailableResponse from 'modules/Payroll/common/demo/providerUnavailable';
import {
    PayrollRunScenario,
    SLOW_SCENARIO_LATENCY_MS,
    resolvePayrollRunFixture,
    resolveRunTeamFixture,
} from 'services/payroll/queries/usePayrollRun/usePayrollRun.mocks';
import { demoPaystubPdf } from 'services/payroll/mutations/useEmployeePaystub/useEmployeePaystub.mocks';
import { PayrollCommand, PayrollCommandAccepted } from 'services/payroll/api/generated/v1/model';

/**
 * The generated client builds this path itself, so the handler owns the literal pattern. A typo
 * here becomes an unhandled request in the demo, which is loud; `usePayrollRun.test.ts` also
 * asserts the pattern against the generated query key.
 */
export const PAYROLL_RUN_URL = '*/api/payroll/v1/payrolls/:payrollUuid/';

const PAYROLL_TEAM_URL = '*/api/payroll/v1/team/';

const PAYROLL_SUBMIT_URL = '*/api/payroll/v1/payrolls/:payrollUuid/submit/';
const PAYROLL_CANCEL_URL = '*/api/payroll/v1/payrolls/:payrollUuid/cancel/';
const PAYROLL_COMMAND_URL = '*/api/payroll/v1/commands/:commandUuid/';

export const EMPLOYEE_PAYSTUB_URL = '*/api/payroll/v1/payrolls/:payrollUuid/workers/:workerUuid/pay-stub/';

/**
 * One handler, six states. `error` answers 503 rather than throwing, because that is what the hook
 * will actually see from a provider outage.
 */
export const buildPayrollRunHandler = (scenario: PayrollRunScenario = 'success') =>
    http.get(PAYROLL_RUN_URL, async () => {
        if (scenario === 'error') {
            return providerUnavailableResponse();
        }

        if (scenario === 'slow') {
            await delay(SLOW_SCENARIO_LATENCY_MS);
        }

        return HttpResponse.json(resolvePayrollRunFixture(scenario));
    });

/**
 * The roster `usePayrollRun` joins in for each row's standing pay. Answered as one page: the
 * accumulation loop is exercised in `Team/demo`, and here the point is that the join has the
 * right people for the scenario — the 28-person run needs its own 28 roster rows.
 */
const buildPayrollTeamHandler = (scenario: PayrollRunScenario = 'success') =>
    http.get(PAYROLL_TEAM_URL, () => {
        const members = resolveRunTeamFixture(scenario);

        return HttpResponse.json({
            as_of: new Date().toISOString(),
            count: members.length,
            employee_count: members.length,
            contractor_count: 0,
            results: members,
        });
    });

/**
 * How the submit plays out. Every command answers 202 and is then polled, so the outcome is a
 * property of the *command* handler, not the submit one — except the refusals, which never mint a
 * command at all. `in_flight` never settles, which is the state to refresh the page on.
 */
type SubmitOutcome =
    | 'confirmed'
    | 'failed'
    | 'in_flight'
    | 'refused_deadline'
    | 'refused_blocked'
    | 'refused_processed'
    | 'already_in_flight'
    | 'already_failed';

/** Polls before the demo's command confirms — long enough to watch the in-flight state. */
const POLLS_BEFORE_SETTLING = 2;

/**
 * How long a story waits for a demo command to settle: it confirms on the poll after
 * `POLLS_BEFORE_SETTLING`, 2s apart, which is well past `findBy*`'s 1s default.
 */
export const COMMAND_SETTLE_TIMEOUT_MS = 8000;

const DEMO_COMMAND_UUID = 'c0mm4nd0-0001-4c3a-9b7e-1f2a3b4c5d61';

const accepted = (): PayrollCommandAccepted => ({
    command_type: 'payroll.submit',
    command_uuid: DEMO_COMMAND_UUID,
    poll_url: `/api/payroll/v1/commands/${DEMO_COMMAND_UUID}/`,
    state: 'pending',
    synced: true,
});

const RESULT_BY_STATE: Record<PayrollCommand['state'], PayrollCommand['result']> = {
    pending: 'unknown',
    sent: 'unknown',
    confirmed: 'success',
    failed: 'failure',
};

const commandIn = (state: PayrollCommand['state'], overrides: Partial<PayrollCommand> = {}): PayrollCommand => ({
    command_uuid: DEMO_COMMAND_UUID,
    command_type: 'payroll.submit',
    subject_type: 'payroll',
    subject_id: '',
    state,
    result: RESULT_BY_STATE[state],
    error_code: '',
    error_message: '',
    reason: '',
    chunk_completed: state === 'pending' ? 0 : 1,
    chunk_total: 1,
    synced: true,
    created_at: new Date().toISOString(),
    sent_at: state === 'pending' ? null : new Date().toISOString(),
    confirmed_at: state === 'confirmed' ? new Date().toISOString() : null,
    created_payroll_uuid: null,
    ...overrides,
});

const refusal = (reason: string, message: string, details: Record<string, unknown> = {}) =>
    HttpResponse.json(
        {
            error: {
                code: 'COMMAND_REFUSED',
                message,
                resolution: 'self_serve',
                retryable: false,
                details: { reason, ...details },
            },
        },
        { status: 422 }
    );

const conflict = (code: string, message: string, details: Record<string, unknown> = {}) =>
    HttpResponse.json({ error: { code, message, resolution: 'wait', retryable: false, details } }, { status: 409 });

/** `POST …/submit/`. The refusals answer here; everything else is a 202 the command handler settles. */
const buildSubmitHandler = (outcome: SubmitOutcome = 'confirmed') =>
    http.post(PAYROLL_SUBMIT_URL, () => {
        switch (outcome) {
            case 'refused_deadline':
                return refusal('deadline_passed', 'The deadline for this payroll has passed.');
            case 'refused_blocked':
                return refusal('payroll_blocked', 'This payroll is blocked.', {
                    blockers: resolvePayrollRunFixture('blocked').blockers,
                });
            case 'refused_processed':
                return refusal('already_processed', 'This payroll has already been submitted.');
            case 'already_in_flight':
                return conflict('COMMAND_IN_FLIGHT', 'An earlier attempt is still being processed.', {
                    command_uuid: DEMO_COMMAND_UUID,
                });
            case 'already_failed':
                return conflict('COMMAND_ALREADY_FAILED', 'That attempt failed. Try again to start a new one.');
            default:
                return HttpResponse.json(accepted(), { status: 202 });
        }
    });

/** `POST …/cancel/`. Accepted like a submit; the command handler settles it. */
const buildCancelHandler = () =>
    http.post(PAYROLL_CANCEL_URL, () =>
        HttpResponse.json({ ...accepted(), command_type: 'payroll.cancel' }, { status: 202 })
    );

/**
 * `GET …/commands/{uuid}/`. Answers `sent` for the first polls, then the outcome — so the
 * in-flight state is on screen long enough to see, and refreshing during it resumes polling.
 */
const buildCommandHandler = (outcome: SubmitOutcome = 'confirmed') => {
    let polls = 0;

    return http.get(PAYROLL_COMMAND_URL, () => {
        polls += 1;

        if (outcome === 'in_flight' || polls <= POLLS_BEFORE_SETTLING) {
            return HttpResponse.json(commandIn('sent'));
        }

        if (outcome === 'failed') {
            return HttpResponse.json(
                commandIn('failed', {
                    error_code: 'PROVIDER_UNAVAILABLE',
                    error_message: 'Your payroll provider could not be reached.',
                })
            );
        }

        return HttpResponse.json(commandIn('confirmed'));
    });
};

/**
 * The paystub actions on the submitted step. `application/pdf` is load-bearing rather than
 * decorative: the View action hands the blob straight to a browser tab, and a blob typed
 * `text/plain` is offered as a download instead of rendering — the demo would then show the
 * opposite of the action that was clicked.
 */
export const buildEmployeePaystubHandler = (scenario: PayrollRunScenario = 'success') =>
    http.get(EMPLOYEE_PAYSTUB_URL, async () => {
        if (scenario === 'error') {
            return HttpResponse.json({ error: { code: 'PAYSTUB_UNAVAILABLE' } }, { status: 502 });
        }

        if (scenario === 'slow') {
            await delay(SLOW_SCENARIO_LATENCY_MS);
        }

        return HttpResponse.arrayBuffer(demoPaystubPdf().buffer as ArrayBuffer, {
            headers: { 'Content-Type': 'application/pdf' },
        });
    });

/**
 * Keyed by endpoint so a story overrides it by name — `{ ...allDemoHandlers, payrollRun: ... }` —
 * rather than relying on array order.
 */
const allDemoHandlers = {
    payrollRun: buildPayrollRunHandler('success'),
    payrollTeam: buildPayrollTeamHandler('success'),
    payrollSubmit: buildSubmitHandler('confirmed'),
    payrollCancel: buildCancelHandler(),
    payrollCommand: buildCommandHandler('confirmed'),
    employeePaystub: buildEmployeePaystubHandler('success'),
};

/** Both halves of one submit outcome — the POST and the poll have to agree on how it ends. */
export const submitHandlersFor = (outcome: SubmitOutcome) => ({
    ...allDemoHandlers,
    payrollSubmit: buildSubmitHandler(outcome),
    payrollCommand: buildCommandHandler(outcome),
});

/** Every handler swapped to one scenario at once — the run and the roster have to agree on who is in it. */
export const demoHandlersFor = (scenario: PayrollRunScenario) => ({
    ...allDemoHandlers,
    payrollRun: buildPayrollRunHandler(scenario),
    payrollTeam: buildPayrollTeamHandler(scenario),
});

export default allDemoHandlers;
